import { useFormik } from "formik";
import { useEffect, useState } from "react";
import MESSAGES from "../../../../common/data/constants/messages";
import { ICustomer } from "../../../../common/data/dtos/customer";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import customerService from "../../../../services/customer-service";

type CustomerFormValues = {
    id?: string;
    name: string;
};

interface Props {
    isModalOpened: boolean;
    selectedCustomer: ICustomer | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const CustomerModal = ({ isModalOpened, selectedCustomer, onModalClose, onAfterSubmit }: Props) => {
    const formik = useFormik({
        initialValues: {
            name: selectedCustomer?.name || '',
        },
        validate: (values) => {
            const errors: {
                name?: string;
            } = {};

            if (!values.name) {
                errors.name = MESSAGES.VALIDATION.REQUIRED_FIELD;
            }

            return errors;
        },
        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        resetForm();

        if (selectedCustomer) {
            setFieldValues(selectedCustomer);
        }
    }, [isModalOpened]);

    const onFormSubmit = async (values: CustomerFormValues) => {
        if (selectedCustomer) {
            setIsSubmitting(true);

            const response = await customerService.updateCustomer({
                id: selectedCustomer.id,
                name: values.name,
                rowVersion: selectedCustomer.rowVersion,
                status: selectedCustomer.status,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {
            setIsSubmitting(true);

            const response = await customerService.createCustomer({
                name: values.name,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                name: ''
            },
        });
    };

    const setFieldValues = (values: ICustomer) => {
        formik.setFieldValue('name', values.name || '');
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='customer-modal'>Manutenção de clientes</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title='Dados do cliente' icon='Store'>
                        <div className='row g-4'>
                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='name' label='Nome' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.name}
                                        invalidFeedback={formik.errors.name}
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};